/**
 * SYR-107 — two fields, one honeypot, and a plainly-stated consent line.
 *
 * Consent is BUNDLED into the submit action rather than sitting behind a
 * checkbox. That is a deliberate call: what makes it defensible is that the
 * wording is conspicuous, that they typed a work email at their own brand, and
 * that we PIN-verify it. The exact wording is a system setting and its hash is
 * stamped onto the lead, so we can always prove what a given person agreed to.
 */

import React, { useState } from 'react';
import { Button, Form, Input } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { THEME } from '../../components/MarketerView/config/theme';

interface Props {
    consentMarkdown: string;
    submitting: boolean;
    onSubmit: (values: { url: string; email: string; companyWebsite: string }) => void;
    fieldError?: { field: string; message: string } | null;
    // True when this is embedded on brandscience.ai, which has already made the
    // pitch in its own hero. Repeating it here reads as a broken page.
    embedded: boolean;
    // The run is switched off. Deliberately invisible to the visitor: the form
    // looks and reads exactly as it does live, and the only thing withheld is
    // the promise of a code, because none will be sent.
    paused?: boolean;
}

export const RequestForm: React.FC<Props> = ({
    consentMarkdown,
    submitting,
    onSubmit,
    fieldError,
    embedded,
    paused = false,
}) => {
    const intl = useIntl();
    const [form] = Form.useForm();
    // Off-screen, never shown, never focusable by a human. A real visitor
    // leaves it empty; a bot fills everything it finds.
    const [honeypot, setHoneypot] = useState('');

    return (
        <div>
            {/* Only when standing alone. The hosting page's hero carries the
                same headline and the same promise word for word, so showing
                both makes the embed look like a rendering fault. */}
            {embedded ? null : (
                <>
                    <h1 style={{ fontSize: 24, fontWeight: 800, color: THEME.textBase, margin: '0 0 8px' }}>
                        <FormattedMessage
                            id="publicHypothesis.form.title"
                            defaultMessage="The Hypothesis"
                        />
                    </h1>
                    <p style={{ fontSize: 15, lineHeight: 1.6, color: '#334155', margin: '0 0 12px' }}>
                        <FormattedMessage
                            id="publicHypothesis.form.subtitle"
                            defaultMessage="This could be what your customers already think about your brand. Here's how to find out for real."
                        />
                    </p>
                    {/* Said up front, before they decide whether to hand over an
                        email. "We have your result and we'll email it to you"
                        reads like a hostage note; the read-out appears here, and
                        the email is a copy rather than the only way to get it. */}
                    <p style={{ fontSize: 15, lineHeight: 1.6, color: '#0f172a', fontWeight: 600, margin: '0 0 24px' }}>
                        <FormattedMessage
                            id="publicHypothesis.form.promise"
                            defaultMessage="You'll read it right here, in about five minutes. We'll email you a copy as well, so you don't have to wait on the page."
                        />
                    </p>
                </>
            )}

            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                onFinish={(values) => onSubmit({ ...values, companyWebsite: honeypot })}
            >
                <Form.Item
                    name="url"
                    label={
                        <FormattedMessage
                            id="publicHypothesis.form.url"
                            defaultMessage="Your website"
                        />
                    }
                    validateStatus={fieldError?.field === 'url' ? 'error' : undefined}
                    help={fieldError?.field === 'url' ? fieldError.message : undefined}
                    rules={[
                        {
                            required: true,
                            message: intl.formatMessage({
                                id: 'publicHypothesis.form.urlRequired',
                                defaultMessage: 'Enter your website address.',
                            }),
                        },
                    ]}
                >
                    <Input size="large" placeholder="yourbrand.com.au" autoComplete="url" />
                </Form.Item>

                <Form.Item
                    name="email"
                    label={
                        <FormattedMessage
                            id="publicHypothesis.form.email"
                            defaultMessage="Your work email"
                        />
                    }
                    validateStatus={fieldError?.field === 'email' ? 'error' : undefined}
                    help={fieldError?.field === 'email' ? fieldError.message : undefined}
                    rules={[
                        {
                            required: true,
                            message: intl.formatMessage({
                                id: 'publicHypothesis.form.emailRequired',
                                defaultMessage: 'Enter your work email address.',
                            }),
                        },
                    ]}
                >
                    <Input size="large" placeholder="you@yourbrand.com.au" autoComplete="email" />
                </Form.Item>

                {/* No code is sent while paused: there is no run to protect,
                    and promising one would leave them watching an inbox. */}
                {paused ? null : (
                    <p
                        style={{
                            fontSize: 13,
                            lineHeight: 1.5,
                            color: THEME.textSecondary,
                            margin: '-8px 0 12px',
                        }}
                    >
                        <FormattedMessage
                            id="publicHypothesis.form.codeNotice"
                            defaultMessage="We'll email you a six-digit code to check you work there. Nothing runs until you enter it."
                        />
                    </p>
                )}

                {/* Honeypot. aria-hidden and off-screen rather than
                    display:none, which some bots specifically skip. */}
                <div
                    aria-hidden="true"
                    style={{ position: 'absolute', left: '-9999px', top: 'auto', height: 0, overflow: 'hidden' }}
                >
                    <label htmlFor="companyWebsite">Company website</label>
                    <input
                        id="companyWebsite"
                        name="companyWebsite"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                    />
                </div>

                <p
                    style={{
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: THEME.textSecondary,
                        margin: '4px 0 16px',
                    }}
                >
                    {consentMarkdown}
                </p>

                <div style={{ textAlign: 'center' }}>
                <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    loading={submitting}
                    style={{
                        background: THEME.primary,
                        borderColor: THEME.primary,
                        borderRadius: 8,
                        height: 'auto',
                        padding: '13px 26px',
                        fontSize: 16,
                        fontWeight: 700,
                        boxShadow: '0 4px 12px rgba(245, 158, 11, .35)',
                    }}
                >
                    <FormattedMessage
                        id="publicHypothesis.form.submit"
                        defaultMessage="Get The Hypothesis"
                    />
                </Button>
                </div>
            </Form>
        </div>
    );
};
